import {
  createContext,
  createEffect,
  For,
  createRoot,
  createSignal,
  getOwner,
  onCleanup,
  type Owner,
  type ParentProps,
  runWithOwner,
  useContext,
  type JSX,
} from "solid-js"
import { Dialog as Kobalte } from "@kobalte/core/dialog"
import { makeEventListener } from "@solid-primitives/event-listener"

type DialogElement = () => JSX.Element

type Active = {
  id: string
  node: JSX.Element
  dispose: () => void
  owner: Owner
  onClose?: () => void
  setClosing: (closing: boolean) => void
}

const Context = createContext<ReturnType<typeof init>>()

function init() {
  const [stack, setStack] = createSignal<Active[]>([])
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const closing = new Set<string>()

  onCleanup(() => {
    for (const timer of timers.values()) clearTimeout(timer)
    timers.clear()
    for (const item of stack()) item.dispose()
    setStack([])
  })

  const active = () => stack().at(-1)

  const close = (id?: string) => {
    const current = id ? stack().find((item) => item.id === id) : active()
    if (!current || closing.has(current.id)) return
    if (id && active()?.id !== id) return
    closing.add(current.id)
    current.onClose?.()
    current.setClosing(true)

    const targetId = current.id
    timers.set(
      targetId,
      setTimeout(() => {
        timers.delete(targetId)
        closing.delete(targetId)
        current.dispose()
        setStack((items) => items.filter((item) => item.id !== targetId))
      }, 100),
    )
  }

  createEffect(() => {
    if (stack().length === 0) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      close()
      event.preventDefault()
      event.stopPropagation()
    }

    makeEventListener(window, "keydown", onKeyDown, { capture: true })
  })

  const show = (element: DialogElement, owner: Owner, onClose?: () => void) => {
    const id = Math.random().toString(36).slice(2)
    let dispose: (() => void) | undefined
    let setClosing: ((closing: boolean) => void) | undefined

    const node = runWithOwner(owner, () =>
      createRoot((d: () => void) => {
        dispose = d
        const [closing, setClosingSignal] = createSignal(false)
        setClosing = setClosingSignal
        return (
          <Kobalte
            modal
            open={!closing()}
            onOpenChange={(open: boolean) => {
              if (open) return
              close(id)
            }}
          >
            <Kobalte.Portal>
              <Kobalte.Overlay data-component="dialog-overlay" onClick={() => close(id)} />
              {element()}
            </Kobalte.Portal>
          </Kobalte>
        )
      }),
    )

    const rootDispose = dispose
    const updateClosing = setClosing
    if (!rootDispose || !updateClosing) return

    setStack((items) => [...items, { id, node, dispose: rootDispose, owner, onClose, setClosing: updateClosing }])
  }

  return {
    get active() {
      return active()
    },
    get stack() {
      return stack()
    },
    close,
    show,
  }
}

export function DialogProvider(props: ParentProps) {
  const ctx = init()
  return (
    <Context.Provider value={ctx}>
      {props.children}
      <div data-component="dialog-stack">
        <For each={ctx.stack}>{(item) => item.node}</For>
      </div>
    </Context.Provider>
  )
}

export function useDialog() {
  const ctx = useContext(Context)
  const owner = getOwner()

  if (!owner) {
    throw new Error("useDialog must be used within a DialogProvider")
  }
  if (!ctx) {
    throw new Error("useDialog must be used within a DialogProvider")
  }

  return {
    get active() {
      return ctx.active
    },
    show(element: DialogElement, onClose?: () => void) {
      const base = ctx.active?.owner ?? owner
      ctx.show(element, base, onClose)
    },
    close() {
      ctx.close()
    },
  }
}
